// Bookie Forest (O'rmon Bag'rida) — variant that renders the standalone
// HTML template (Bookie-formula wedding card, warm-cream paper + deep
// forest green #1F3B2E + gold #B89968 highlights, Cormorant Garamond
// for display + body + Pinyon Script accent, paired fern-frond corner
// ornaments in the HERO + fern-leaf divider between sections) inside
// a full-bleed iframe. The HTML lives at /templates/bookie-forest.html
// and ships with an inline customizer script — it reads names / date /
// venue / address / time from the iframe URL and walks the document's
// text nodes to swap the mocks before first paint. The countdown picks
// up window.__INV_TARGET__ at the same time.
//
// Date is forwarded as dd.mm.yyyy because the customizer's regex
// (/^\d{2}\.\d{2}\.\d{4}$/) is what parses it into the long-form
// "21 Mart, 2026" + Uzbek weekday + countdown target — converting to
// ISO would silently break that pipeline. Payload via URL (not
// postMessage) because the customizer needs to run before the
// IntersectionObserver / countdown scripts inside the HTML attach.
//
// Cache buster `v` should be bumped any time the static HTML changes —
// Vite doesn't hash files in public/, so without it the browser will
// happily serve a stale customizer.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/bookie-forest.html";

export function BookieForestStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) params.set("date", t.date);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.time) params.set("time", t.time);
  // Demo / preview surfaces (gallery thumbnail, editor preview) leave
  // ctx.slug undefined — that's the signal to keep the seeded mock
  // wishes around so visitors browsing the catalog see what a
  // populated guestbook looks like.
  if (ctx.slug) params.set("slug", ctx.slug);
  // Forward chosen background music — iOS Safari only unlocks <audio>
  // when the gesture happens in the SAME document, so the player has
  // to live inside the iframe.
  if (t.musicTrackId) params.set("audio", `/music/${t.musicTrackId}.mp3`);
  params.set("v", "2");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "O'rmon Bag'rida"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#F6EFE0",
      }}
    />
  );
}
